import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { PlusIcon, TrashIcon, CoinsIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CurrencyLibrary = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: '', symbol: '' });
  const [processing, setProcessing] = useState(false);
  
  // Base currency state
  const [baseCurrency, setBaseCurrency] = useState('TRY');
  const [updatingBaseCurrency, setUpdatingBaseCurrency] = useState(false);

  // Major world currencies (convertible)
  const MAJOR_CURRENCIES = [
    { code: 'TRY', symbol: '₺', name: 'Türk Lirası' },
    { code: 'USD', symbol: '$', name: 'ABD Doları' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'İngiliz Sterlini' },
    { code: 'JPY', symbol: '¥', name: 'Japon Yeni' },
    { code: 'CHF', symbol: 'Fr', name: 'İsviçre Frangı' },
    { code: 'CAD', symbol: 'C$', name: 'Kanada Doları' },
    { code: 'AUD', symbol: 'A$', name: 'Avustralya Doları' },
    { code: 'CNY', symbol: '¥', name: 'Çin Yuanı' },
    { code: 'SEK', symbol: 'kr', name: 'İsveç Kronu' },
    { code: 'NOK', symbol: 'kr', name: 'Norveç Kronu' },
    { code: 'DKK', symbol: 'kr', name: 'Danimarka Kronu' },
    { code: 'PLN', symbol: 'zł', name: 'Polonya Zlotisi' },
    { code: 'CZK', symbol: 'Kč', name: 'Çek Kronu' },
    { code: 'HUF', symbol: 'Ft', name: 'Macar Forinti' },
    { code: 'RUB', symbol: '₽', name: 'Rus Rublesi' },
    { code: 'BRL', symbol: 'R$', name: 'Brezilya Reali' },
    { code: 'MXN', symbol: '$', name: 'Meksika Pesosu' },
    { code: 'INR', symbol: '₹', name: 'Hint Rupisi' },
    { code: 'KRW', symbol: '₩', name: 'Güney Kore Wonu' },
    { code: 'ZAR', symbol: 'R', name: 'Güney Afrika Randı' },
    { code: 'SGD', symbol: 'S$', name: 'Singapur Doları' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Doları' },
    { code: 'NZD', symbol: 'NZ$', name: 'Yeni Zelanda Doları' },
    { code: 'THB', symbol: '฿', name: 'Tayland Bahtı' },
    { code: 'MYR', symbol: 'RM', name: 'Malezya Ringgiti' },
    { code: 'IDR', symbol: 'Rp', name: 'Endonezya Rupisi' },
    { code: 'PHP', symbol: '₱', name: 'Filipin Pesosu' },
    { code: 'AED', symbol: 'د.إ', name: 'BAE Dirhemi' },
    { code: 'SAR', symbol: '﷼', name: 'Suudi Arabistan Riyali' }
  ];

  // Initialize major currencies
  const handleInitializeCurrencies = async () => {
    try {
      setProcessing(true);
      await axiosInstance.post(`${API}/api/currencies/initialize-major`);
      
      toast.success('Tüm major para birimleri başarıyla eklendi');
      fetchCurrencies();
    } catch (error) {
      console.error('Error initializing currencies:', error);
      toast.error(error.response?.data?.detail || 'Para birimleri eklenirken hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  // Fetch currencies and base currency on component mount
  useEffect(() => {
    fetchCurrencies();
    fetchBaseCurrency();
  }, []);

  const fetchBaseCurrency = async () => {
    try {
      const response = await axiosInstance.get(`/company/base-currency`);
      setBaseCurrency(response.data.base_currency);
    } catch (error) {
      console.error('Error fetching base currency:', error);
      toast.error('Temel para birimi yüklenirken hata oluştu');
    }
  };

  const handleUpdateBaseCurrency = async (newBaseCurrency) => {
    try {
      setUpdatingBaseCurrency(true);
      await axiosInstance.put(`/company/base-currency`, {
        base_currency: newBaseCurrency
      });
      
      setBaseCurrency(newBaseCurrency);
      toast.success('Temel para birimi güncellendi');
    } catch (error) {
      console.error('Error updating base currency:', error);
      toast.error(error.response?.data?.detail || 'Temel para birimi güncellenirken hata oluştu');
    } finally {
      setUpdatingBaseCurrency(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API}/api/currencies`);
      console.log('✅ Currencies fetched:', response.data);
      setCurrencies(response.data || []);
    } catch (error) {
      console.error('❌ Error fetching currencies:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Para birimleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCurrency = async () => {
    if (!newCurrency.code.trim() || !newCurrency.symbol.trim()) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      setProcessing(true);
      await axiosInstance.post(`${API}/api/currencies`, {
        code: newCurrency.code.toUpperCase(),
        symbol: newCurrency.symbol
      });
      
      toast.success('Para birimi başarıyla eklendi');
      setShowAddModal(false);
      setNewCurrency({ code: '', symbol: '' });
      fetchCurrencies();
    } catch (error) {
      console.error('Error adding currency:', error);
      toast.error(error.response?.data?.detail || 'Para birimi eklenirken hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteCurrency = async (currencyId, currencyCode, isDefault) => {
    if (isDefault) {
      toast.error('Varsayılan para birimleri silinemez');
      return;
    }

    if (!window.confirm(`${currencyCode} para birimini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`${API}/api/currencies/${currencyId}`);
      toast.success('Para birimi silindi');
      fetchCurrencies();
    } catch (error) {
      console.error('Error deleting currency:', error);
      toast.error(error.response?.data?.detail || 'Para birimi silinirken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Para birimleri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <CoinsIcon className="w-6 h-6 mr-2 text-blue-600" />
            Para Birimleri
          </h2>
          <p className="text-gray-600 mt-1">
            Sistemde kullanılacak para birimlerini yönetin
          </p>
        </div>
        <div className="flex space-x-3">
          {currencies.length === 0 && (
            <Button
              onClick={handleInitializeCurrencies}
              disabled={processing}
              className="flex items-center bg-green-600 hover:bg-green-700 text-white"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Ekleniyor...
                </>
              ) : (
                <>
                  <CoinsIcon className="w-4 h-4 mr-2" />
                  Tüm Major Para Birimlerini Ekle ({MAJOR_CURRENCIES.length})
                </>
              )}
            </Button>
          )}
          <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center bg-blue-600 hover:bg-blue-700 text-white"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Yeni Para Birimi
          </Button>
        </div>
      </div>

      {/* Base Currency Selection */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
              🌟 Temel Para Birimi
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Şirketinizin tüm hesaplamalarında kullanılacak ana para birimidir. 
              Tüm raporlar ve özetler bu para birimine göre gösterilir.
            </p>
            
            <div className="flex items-center space-x-4">
              <Label className="text-sm font-medium text-gray-700">Temel Para Birimi:</Label>
              <select
                value={baseCurrency}
                onChange={(e) => handleUpdateBaseCurrency(e.target.value)}
                disabled={updatingBaseCurrency || currencies.length === 0}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50"
              >
                {currencies.length === 0 ? (
                  <option value="">Para birimi yükleniyor...</option>
                ) : (
                  currencies.map((currency) => (
                    <option key={currency.id} value={currency.code}>
                      {currency.symbol} {currency.code}
                    </option>
                  ))
                )}
              </select>
              
              {updatingBaseCurrency && (
                <div className="flex items-center text-sm text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Güncelleniyor...
                </div>
              )}
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                ⚠️ <strong>Önemli:</strong> Temel para birimini değiştirmek mevcut raporları etkileyebilir. 
                Bu değişiklik tüm kullanıcılar için geçerli olacaktır.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Currencies List */}
      <Card className="p-6">
        {currencies.length === 0 ? (
          <div className="text-center py-12">
            <CoinsIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Henüz para birimi eklenmemiş</p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="mt-4"
              variant="outline"
            >
              İlk Para Birimini Ekle
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kod
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sembol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currencies.map((currency) => (
                  <tr key={currency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {currency.code}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-lg text-gray-900">
                        {currency.symbol}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {currency.is_default ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Varsayılan
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Özel
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!currency.is_default && (
                        <Button
                          onClick={() => handleDeleteCurrency(currency.id, currency.code, currency.is_default)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Currency Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Para Birimi Ekle</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="code">Para Birimi Kodu *</Label>
              <Input
                id="code"
                placeholder="Örn: JPY, CNY, CHF"
                value={newCurrency.code}
                onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                maxLength={3}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">3 harfli ISO kodu (Örn: EUR, USD)</p>
            </div>
            
            <div>
              <Label htmlFor="symbol">Para Birimi Sembolü *</Label>
              <Input
                id="symbol"
                placeholder="Örn: ¥, ¥, ₣"
                value={newCurrency.symbol}
                onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                maxLength={5}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">Para birimi sembolü (Örn: €, $, ₺)</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setNewCurrency({ code: '', symbol: '' });
              }}
              disabled={processing}
            >
              İptal
            </Button>
            <Button
              onClick={handleAddCurrency}
              disabled={processing || !newCurrency.code.trim() || !newCurrency.symbol.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {processing ? 'Ekleniyor...' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrencyLibrary;
